import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Download, Filter, RefreshCw, X } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  DateRangePicker,
  DateRangePickerPresetKey,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  buildDateRangePickerPresets,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { ChangelogFilters } from '../ChangelogFilters';
import { downloadChangelogCsv } from '../../csvExport';
import { useChangelogExport, useChangelogQuery } from '../../../../changelog/hooks';
import {
  ChangelogCategory,
  ChangelogSortDirection,
  ChangelogSortProperty,
  ChangelogType,
  changelogPageSizes,
  type ChangelogEntry,
} from '../../../../changelog/model';
import {
  changelogCategoryLabel,
  changelogContextLabel,
  changelogFieldLabel,
  displayChangelogValue,
  formatChangelogDate,
} from '../../../../changelog/presentation';
import { countActiveChangelogFilters } from '../../changelogRouteSearch';
import { useChangelogRouteFilters } from '../../useChangelogRouteFilters';

const datePresets = buildDateRangePickerPresets(
  [
    DateRangePickerPresetKey.Yesterday,
    DateRangePickerPresetKey.Last7Days,
    DateRangePickerPresetKey.ThisWeek,
    DateRangePickerPresetKey.ThisMonth,
    DateRangePickerPresetKey.LastMonth,
  ],
  { endPeriodsYesterday: true },
);

export function ChangelogTable() {
  const { can } = useShigoPermissions();
  const { filter, resetFilters, setFilters, setPagination, setSort } = useChangelogRouteFilters();
  const query = useChangelogQuery(filter);
  const exportMutation = useChangelogExport();
  const activeFilterCount = countActiveChangelogFilters(filter);
  const [filtersOpen, setFiltersOpen] = useState(activeFilterCount > 0);
  const columns = buildDashboardDataTableColumns<ChangelogEntry>([
    {
      id: 'createdOn',
      kind: DashboardDataTableColumnKind.Date,
      header: dashboardT('changelog.date'),
      headerClassName: 'w-48 pl-4',
      className: 'w-48 pl-4 whitespace-nowrap',
      value: (entry) => formatChangelogDate(entry.createdOn),
      sortable: true,
      sortKey: ChangelogSortProperty.CreatedOn,
    },
    {
      id: 'category',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('changelog.category'),
      headerClassName: 'w-56',
      className: 'w-56',
      cell: (entry) => <ChangelogCategoryCell entry={entry} />,
    },
    {
      id: 'type',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('changelog.type'),
      headerClassName: 'w-28',
      className: 'w-28 font-medium',
      value: (entry) => typeLabel(entry.type),
    },
    {
      id: 'fieldName',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('changelog.field'),
      headerClassName: 'w-48',
      className: 'w-48',
      cell: (entry) => truncatedValue(changelogFieldLabel(entry)),
    },
    {
      id: 'oldValue',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('changelog.oldValue'),
      headerClassName: 'w-64',
      className: 'w-64',
      cell: (entry) => truncatedValue(displayChangelogValue(entry.oldValue)),
    },
    {
      id: 'arrow',
      kind: DashboardDataTableColumnKind.Text,
      header: '',
      headerClassName: 'w-10 text-center',
      className: 'w-10 text-center',
      value: () => '→',
    },
    {
      id: 'newValue',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('changelog.newValue'),
      headerClassName: 'w-64',
      className: 'w-64',
      cell: (entry) => truncatedValue(displayChangelogValue(entry.newValue)),
    },
    {
      id: 'createdBy',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('changelog.user'),
      headerClassName: 'w-56 pr-4',
      className: 'w-56 pr-4',
      cell: (entry) => truncatedValue(entry.createdBy ?? '-'),
    },
  ]);

  return (
    <main className="page-gutter min-w-0 py-6">
      <DashboardDataTable
        title={dashboardT('changelog.title')}
        tableLabel={dashboardT('changelog.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(entry) => String(entry.id)}
        loading={query.isFetching}
        loadingMessage={dashboardT('changelog.loading')}
        emptyMessage={dashboardT('changelog.empty')}
        errorMessage={query.isError ? dashboardT('changelog.loadError') : undefined}
        minWidthClassName="min-w-[1480px]"
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === ChangelogSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (sort) => {
            if (!sort) {
              setSort(null, filter.sortDirection);
              return;
            }
            setSort(
              ChangelogSortProperty.CreatedOn,
              sort.direction === DashboardDataTableSortDirection.Asc
                ? ChangelogSortDirection.Asc
                : ChangelogSortDirection.Desc,
            );
          },
        }}
        toolbar={
          <>
            <DateRangePicker
              className="w-full justify-start sm:w-auto"
              label={dashboardT('changelog.dateRange')}
              maxDate={new Date()}
              popoverAlign="start"
              presets={datePresets}
              triggerClassName="max-w-none sm:min-w-56"
              value={{ dateFrom: filter.dateFrom, dateTo: filter.dateTo }}
              onChange={({ dateFrom, dateTo }) => {
                setFilters({ dateFrom, dateTo });
              }}
            />
            <div className="flex items-center">
              <Button
                type="button"
                variant="outline"
                className={activeFilterCount ? 'rounded-r-none' : undefined}
                aria-expanded={filtersOpen}
                aria-controls="changelog-filter-toolbar"
                onClick={() => {
                  setFiltersOpen((open) => !open);
                }}
              >
                <Filter className="h-4 w-4" aria-hidden="true" />
                {dashboardT('changelog.filters')}
                {activeFilterCount ? (
                  <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                    {activeFilterCount}
                  </span>
                ) : null}
              </Button>
              {activeFilterCount ? (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive rounded-l-none border-l-0"
                  aria-label={dashboardT('changelog.clearFilters')}
                  onClick={resetFilters}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              ) : null}
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={dashboardT('changelog.refresh')}
              disabled={query.isFetching}
              onClick={() => {
                void query.refetch();
              }}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            </Button>
            {can(ShigoPermission.AdminChangelogExport) ? (
              <Button
                type="button"
                variant="outline"
                disabled={exportMutation.isPending}
                onClick={() => {
                  void exportMutation
                    .mutateAsync(filter)
                    .then((entries) => {
                      if (!entries.length) {
                        toast.error(dashboardT('changelog.exportEmpty'));
                        return;
                      }
                      downloadChangelogCsv(entries);
                    })
                    .catch((error: unknown) => {
                      showRequestError(error, dashboardT('changelog.exportError'));
                    });
                }}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {dashboardT(exportMutation.isPending ? 'changelog.exporting' : 'changelog.export')}
              </Button>
            ) : null}
          </>
        }
        filterToolbar={
          filtersOpen ? (
            <div id="changelog-filter-toolbar">
              <ChangelogFilters filter={filter} onChange={setFilters} />
            </div>
          ) : undefined
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: changelogPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
      />
    </main>
  );
}

function ChangelogCategoryCell({ entry }: { entry: ChangelogEntry }) {
  const content = (
    <>
      <span className="block truncate font-semibold">{changelogContextLabel(entry)}</span>
      <span className="text-muted-foreground block truncate text-xs">
        {changelogCategoryLabel(entry.category)}
      </span>
    </>
  );
  const linkClassName = 'text-primary block min-w-0 hover:underline';

  switch (entry.category) {
    case ChangelogCategory.Placement: {
      const placementId = contextId(entry, 'placementId');
      return placementId ? (
        <Link
          className={linkClassName}
          to="/supply/placements/edit/$placementId"
          params={{ placementId }}
        >
          {content}
        </Link>
      ) : (
        <div className="min-w-0">{content}</div>
      );
    }
    case ChangelogCategory.Publisher: {
      const publisherId = contextId(entry, 'publisherId');
      return publisherId ? (
        <Link
          className={linkClassName}
          to="/supply/publishers/edit/$publisherId"
          params={{ publisherId }}
        >
          {content}
        </Link>
      ) : (
        <div className="min-w-0">{content}</div>
      );
    }
    case ChangelogCategory.App: {
      const appId = contextId(entry, 'appId');
      return appId ? (
        <Link className={linkClassName} to="/supply/apps/edit/$appId" params={{ appId }}>
          {content}
        </Link>
      ) : (
        <div className="min-w-0">{content}</div>
      );
    }
    case ChangelogCategory.Integration: {
      const integrationId = contextId(entry, 'integrationId');
      const demandPartnerId = contextId(entry, 'integrationDpId');
      return integrationId && demandPartnerId ? (
        <Link
          className={linkClassName}
          to="/demand/integrations/edit/$demandPartnerId/$integrationId"
          params={{ demandPartnerId, integrationId }}
        >
          {content}
        </Link>
      ) : (
        <div className="min-w-0">{content}</div>
      );
    }
    case ChangelogCategory.LineItem: {
      const lineItemId = contextId(entry, 'lineItemId');
      const campaignId =
        contextId(entry, 'lineItemCampaignId') ??
        contextId(entry, 'campaignId') ??
        contextId(entry, 'lineItemOrderId');
      return lineItemId && campaignId ? (
        <Link
          className={linkClassName}
          to="/campaigns/campaigns/overview/$campaignId/line-items/edit/$lineItemId"
          params={{ campaignId, lineItemId }}
        >
          {content}
        </Link>
      ) : (
        <div className="min-w-0">{content}</div>
      );
    }
    case ChangelogCategory.Deal: {
      const dealId = contextId(entry, 'dealId');
      return dealId ? (
        <Link className={linkClassName} to="/demand/deals/edit/$dealId" params={{ dealId }}>
          {content}
        </Link>
      ) : (
        <div className="min-w-0">{content}</div>
      );
    }
    case ChangelogCategory.Targeting: {
      const targetingId = contextId(entry, 'targetingId');
      return targetingId ? (
        <Link className={linkClassName} to="/targeting/$targetingId/edit" params={{ targetingId }}>
          {content}
        </Link>
      ) : (
        <div className="min-w-0">{content}</div>
      );
    }
    case ChangelogCategory.DemandPartner: {
      const demandPartnerId = contextId(entry, 'demandPartnerId');
      return demandPartnerId ? (
        <Link
          className={linkClassName}
          to="/demand/partners/edit/$demandPartnerId"
          params={{ demandPartnerId }}
        >
          {content}
        </Link>
      ) : (
        <div className="min-w-0">{content}</div>
      );
    }
    default:
      return <div className="min-w-0">{content}</div>;
  }
}

function contextId(entry: ChangelogEntry, key: string) {
  const value = entry.context[key];
  if (value === null || value === undefined) return undefined;
  const normalized = String(value).trim();
  return normalized || undefined;
}

function truncatedValue(value: string) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span className="block truncate">{value}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-96 break-words whitespace-pre-wrap">{value}</TooltipContent>
    </Tooltip>
  );
}

function typeLabel(type: ChangelogType) {
  const keys = {
    [ChangelogType.Create]: 'changelog.types.create',
    [ChangelogType.Update]: 'changelog.types.update',
    [ChangelogType.Delete]: 'changelog.types.delete',
  } as const;
  return dashboardT(keys[type]);
}
